/**
 * @file     QuizSection.tsx
 * @readme   ../README.md
 * @purpose  Quiz section that renders multiple quiz questions sequentially with scoring.
 */
'use client';

import '@/core/i18n/deferred/academy';
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import type { QuizQuestion } from '@/content-system/academy/types';
import { QuizCard } from './QuizCard';

interface QuizSectionProps {
  questions: QuizQuestion[];
  lessonId: string;
  onQuizPassed?: () => void;
}

export function QuizSection({ questions, lessonId, onQuizPassed }: QuizSectionProps) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const answeredCount = Object.keys(answers).length;
  const correctCount = Object.entries(answers).filter(
    ([qId, ansIdx]) => questions.find((q) => q.id === qId)?.correctIndex === ansIdx,
  ).length;
  const allAnswered = answeredCount === questions.length;

  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
    setAnswers({});
  }, [lessonId]);
  useEffect(() => {
    if (allAnswered && !firedRef.current) {
      firedRef.current = true;
      onQuizPassed?.();
    }
  }, [allAnswered, onQuizPassed]);

  const handleAnswer = (questionId: string, selectedIndex: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedIndex }));
  };

  return (
    <Box
      component="section"
      aria-label={t('academy.quiz.label', { defaultValue: 'Check your understanding' })}
      sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 4, borderTop: hairline }}
    >
      {questions.map((q, i) => (
        <QuizCard
          key={q.id}
          question={q}
          selectedIndex={answers[q.id] ?? null}
          onSelect={(idx) => handleAnswer(q.id, idx)}
          label={t('academy.quiz.counter', {
            defaultValue: 'Check your understanding · {{n}} of {{total}}',
            n: i + 1,
            total: questions.length,
          })}
        />
      ))}

      {allAnswered && (
        <Box role="status" sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
          <Box component="p" sx={{ m: 0, fontSize: 17, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
            {t('academy.quiz.scoreLine', { defaultValue: '{{correct}} of {{total}} right', correct: correctCount, total: questions.length })}
          </Box>
          <Box component="p" sx={{ m: 0, fontSize: 14, color: hubColors.textSecondary }}>
            {correctCount === questions.length
              ? t('academy.quiz.perfect', { defaultValue: 'All correct.' })
              : correctCount >= questions.length * 0.7
                ? t('academy.quiz.great', { defaultValue: 'Well done.' })
                : t('academy.quiz.reviewNotes', { defaultValue: 'The notes above explain each answer.' })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
